import React, {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { pdfjs } from 'react-pdf/dist/esm/entry.webpack5';
import { MARGIN_BOTTOM, PADDING } from '../../common/componentUtils';
import { DefaultLoader } from './DefaultLoader';
import { useResizeObserver } from './hooks/useResizeObserver';
import { DocumentContainer, PDFViewerContainer } from './PDFViewer.styled';
import { PDFViewerDocument } from './PDFViewerDocument';
import { PDFViewerNavBar } from './PDFViewerNavBar';
import { PDFViewerVariableSizeList } from './PDFViewerVariableSizeList';
import { PDFViewerProps } from './types';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export const PDFViewer = memo(
	forwardRef<any, PDFViewerProps>(
		(
			{
				pdf: pdfFile,
				containerHeight,
				onPageClick,
				pageHeight,
				overlay,
				loader,
				highlightClickedPage,
				showNavigationBar,
				scrollToPageNumber = 1,
				maxPageWidth,
				pageContainerStyle = {},
				pageStyle = {},
				renderTextLayer,
				renderAnnotationLayer,
			},
			ref
		) => {
			const listRef = useRef(null);
			const documentRef = useRef<HTMLDivElement>(null);
			const hasDifferentPageSizesRef = useRef(false);
			const [numPages, setNumPages] = useState<number>(0);
			const maxScale = useRef<number | null>(0);

			const [pageIndexRatio, setPageIndexRatio] = useState<Map<any, any> | null>(null);
			const [selectedPage, setSelectedPage] = useState(1);
			const [currentPage, setCurrentPage] = useState(1);
			const [pageWidth, setPageWidth] = useState(0);
			const [resizedContainerHeight, setResizedContainerHeight] =
				useState(containerHeight);

			const [zoomFactor, setZoomFactor] = React.useState(1);
			const zoomIncrement = 0.1;

			const handleZoomInButtonClick = () => {
				setZoomFactor(Math.min(zoomFactor + zoomIncrement, 5));
			};

			const handleZoomOutButtonClick = () => {
				setZoomFactor(Math.max(zoomFactor - zoomIncrement, 0.1));
			};

			const computePageWidth = useCallback((newPageWidth: number) => {
				if (pageHeight) {
					newPageWidth = pageHeight / maxScale.current;
				}

				if (maxPageWidth) {
					newPageWidth = newPageWidth < maxPageWidth ? newPageWidth : maxPageWidth;
				}

				setPageWidth(newPageWidth);
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, []);

			const onDocumentLoadSuccess = data => {
				const { hasDifferentPageSizes, scale, pageAspectRatioMap, totalPages } = data;

				hasDifferentPageSizesRef.current = hasDifferentPageSizes;
				maxScale.current = scale;

				if (documentRef.current) {
					setPageIndexRatio(pageAspectRatioMap);
					setNumPages(totalPages);
				}
			};

			const rowHeight = useRef(0);
			const computeRowHeight = useCallback(() => {
				if (pageHeight) {
					return pageHeight + PADDING;
				}
				const documentWidth = documentRef.current!.offsetWidth;
				rowHeight.current = Math.ceil(
					maxScale.current! * documentWidth +
						(hasDifferentPageSizesRef.current ? MARGIN_BOTTOM : PADDING)
				);
				return rowHeight.current;
			}, [pageHeight]);

			const onPageChange = (num: number) => {
				setCurrentPage(num);
				if (listRef.current) {
					(listRef.current as any).scrollToItem(num - 1);
				}
			};

			const pdfLoader = loader || (
				<DefaultLoader
					style={{
						height: containerHeight,
					}}
				/>
			);

			useEffect(() => {
				if (listRef.current) {
					(listRef.current as any).scrollToItem(scrollToPageNumber - 1);
					setCurrentPage(scrollToPageNumber);
				}
			}, [scrollToPageNumber]);

			useImperativeHandle(
				ref,
				() => ({
					scrollToPage: (pageNumber: number) => {
						if (listRef.current) {
							(listRef.current as any).scrollToItem(pageNumber - 1);
							setCurrentPage(pageNumber);
						}
					},
					getCurrentPage: () => {
						return currentPage;
					},
				}),
				[currentPage]
			);

			useResizeObserver({
				documentRef,
				listRef,
				pdfFile,
				computePageWidth,
				setResizedContainerHeight,
			});

			return (
				<PDFViewerContainer style={{ ...pageContainerStyle }}>
					<DocumentContainer
						ref={documentRef}
						data-testid="viewer-container"
						scale={`${zoomFactor}`}
					>
						<PDFViewerDocument
							file={pdfFile}
							onLoadSuccess={onDocumentLoadSuccess}
							loading={pdfLoader}
						>
							<PDFViewerVariableSizeList
								ref={listRef}
								height={resizedContainerHeight}
								itemCount={numPages}
								itemSize={computeRowHeight}
								rowHeight={rowHeight.current}
								currentPage={currentPage}
								setCurrentPage={setCurrentPage}
								itemData={{
									pageWidth,
									hasDifferentPageSizes: hasDifferentPageSizesRef.current,
									pageIndexRatio,
									overlay,
									onPageClick,
									selectedPage,
									setSelectedPage,
									highlightClickedPage,
									pageStyle,
									renderTextLayer,
									renderAnnotationLayer,
									pdfLoader,
								}}
							/>
						</PDFViewerDocument>
						{showNavigationBar && (
							<PDFViewerNavBar
								onZoomIn={handleZoomInButtonClick}
								onZoomOut={handleZoomOutButtonClick}
								zoomFactor={zoomFactor}
								currentPage={currentPage}
								totalPages={numPages}
								onPageChange={onPageChange}
							/>
						)}
					</DocumentContainer>
				</PDFViewerContainer>
			);
		}
	)
);
